import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CorpHeader } from "@/components/CorpHeader";
import { adminApi, formatApiError, fileUrl } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Building2, TrendingUp, AlertCircle, Plus, ExternalLink,
  CheckCircle2, Ban, KeyRound, Pencil, Trash2, MoreHorizontal, Search,
  BookOpen, FileText, Link2, QrCode, Inbox, Phone, Mail,
} from "lucide-react";
import { FileUpload } from "@/components/FileUpload";
import { uploadAdminFile } from "@/lib/api";

import { TeamPanel } from "@/components/TeamPanel";

function EnquiryInbox() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeSpam, setIncludeSpam] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/landing-enquiries${includeSpam ? "?include_spam=true" : ""}`);
      setRows(r.data?.entries || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load enquiries");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeSpam]);

  const del = async (row) => {
    if (!window.confirm(`Delete enquiry from ${row.name}? This cannot be undone.`)) return;
    setDeletingId(row.id);
    try {
      await adminApi.delete(`/admin/landing-enquiries/${row.id}`);
      setRows(prev => prev.filter(r => r.id !== row.id));
      toast.success("Enquiry deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally { setDeletingId(null); }
  };

  return (
    <Card className="mb-8 border-stone-200/80" data-testid="enquiry-inbox">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded bg-emerald-900/5 text-emerald-800 flex items-center justify-center">
              <Inbox className="w-4 h-4" />
            </div>
            <div>
              <h2 className="font-serif text-xl">Agent enquiries</h2>
              <p className="caption-label mt-0.5">Leads submitted via the landing page</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="caption-label text-stone-500 inline-flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeSpam}
                onChange={(e) => setIncludeSpam(e.target.checked)}
                className="accent-emerald-800"
                data-testid="enquiry-include-spam"
              />
              Show trapped spam
            </label>
            <Button variant="outline" size="sm" onClick={load} data-testid="enquiry-refresh">
              Refresh
            </Button>
          </div>
        </div>
        {loading ? (
          <div className="py-8 text-center text-sm text-stone-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-sm text-stone-500">
            No enquiries yet — leads will appear here as soon as the landing page form is submitted.
          </div>
        ) : (
          <div className="divide-y divide-stone-100">
            {rows.map((row, idx) => (
              <div
                key={row.id}
                className="py-3 flex items-start justify-between gap-4"
                data-testid={`enquiry-row-${idx}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-stone-900">{row.name}</span>
                    <span className="text-stone-400">·</span>
                    <span className="text-sm text-stone-600">
                      {row.enquiry_type === "shop"
                        ? [row.shop_name, row.zip_code].filter(Boolean).join(" · ") || "—"
                        : row.region}
                    </span>
                    <span className={`inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 font-mono ${row.enquiry_type === "shop" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                      {row.enquiry_type === "shop" ? "shop" : "agent"}
                    </span>
                    {row.is_spam && (
                      <span className="inline-flex items-center gap-1 text-[11px] bg-red-100 text-red-700 rounded-full px-2 py-0.5 font-mono">
                        spam · {row.spam_reason}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-stone-500 font-mono mt-0.5 flex items-center gap-3 flex-wrap">
                    <a href={`tel:${row.phone}`} className="inline-flex items-center gap-1 hover:text-stone-800">
                      <Phone className="w-3 h-3" /> {row.phone}
                    </a>
                    <span>{formatEnquiryDate(row.created_at)}</span>
                    {row.ip && <span>· IP {row.ip}</span>}
                  </div>
                  {row.message && (
                    <p className="text-sm text-stone-700 mt-2 whitespace-pre-wrap bg-stone-50 border border-stone-100 rounded px-3 py-2">
                      {row.message}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    asChild
                    variant="ghost"
                    size="sm"
                    title="Reply by email"
                    data-testid={`enquiry-mail-${idx}`}
                  >
                    <a href={`mailto:?subject=Re:%20Relax-MIS%20enquiry&body=Hi%20${encodeURIComponent(row.name)}%2C%0A%0A`}>
                      <Mail className="w-3.5 h-3.5" />
                    </a>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => del(row)}
                    disabled={deletingId === row.id}
                    title="Delete enquiry"
                    className="text-red-600 hover:bg-red-50"
                    data-testid={`enquiry-delete-${idx}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formatEnquiryDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

function fmtTHB(n) { return `฿${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
function fmtTHBn(n) { return `฿${Math.round(Number(n || 0)).toLocaleString("en-US")}`; }

function ShopForm({ open, onOpenChange, onSaved, initial }) {
  const { t } = useLang();
  const isEdit = !!initial?.id;
  const [name, setName] = useState(initial?.name || "");
  const [address, setAddress] = useState(initial?.address || "");
  const [area, setArea] = useState(initial?.area || "");
  const [town, setTown] = useState(initial?.town || "");
  const [logoPath, setLogoPath] = useState(initial?.logo_path || null);
  const [ownerName, setOwnerName] = useState(initial?.owner_name || "");
  const [ownerEmail, setOwnerEmail] = useState(initial?.owner_email || "");
  const [ownerPin, setOwnerPin] = useState("");
  const [managers, setManagers] = useState(initial?.managers?.length ? initial.managers.map(m => ({...m, pin: ""})) : [{ name: "", email: "", pin: "" }]);
  const [renewal, setRenewal] = useState(initial?.renewal_date || new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10));
  const [fee, setFee] = useState(initial?.monthly_fee_thb ?? 1500);
  const [openTime, setOpenTime] = useState(initial?.opening_time_24h || "08:00");
  const [closeTime, setCloseTime] = useState(initial?.closing_time_24h || "23:00");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(initial?.name || "");
    setAddress(initial?.address || "");
    setArea(initial?.area || "");
    setTown(initial?.town || "");
    setLogoPath(initial?.logo_path || null);
    setOwnerName(initial?.owner_name || "");
    setOwnerEmail(initial?.owner_email || "");
    setOwnerPin("");
    setManagers(initial?.managers?.length ? initial.managers.map(m => ({...m, pin: ""})) : [{ name: "", email: "", pin: "" }]);
    setRenewal(initial?.renewal_date || new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10));
    setFee(initial?.monthly_fee_thb ?? 1500);
    setOpenTime(initial?.opening_time_24h || "08:00");
    setCloseTime(initial?.closing_time_24h || "23:00");
    setErr("");
  }, [open, initial]);

  const slugPreview = useMemo(() => {
    const raw = `${name}-${address}-${town}`.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
    return raw;
  }, [name, address, town]);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr("");
    try {
      const validMgrs = managers.filter(m => m.name || m.pin);
      if (isEdit) {
        const payload = {
          name, address, area, town, logo_path: logoPath,
          owner_name: ownerName, owner_email: ownerEmail || undefined,
          renewal_date: renewal, monthly_fee_thb: Number(fee),
          opening_time_24h: openTime, closing_time_24h: closeTime,
          managers: validMgrs.map(m => ({ id: m.id, name: m.name, email: m.email || undefined, ...(m.pin ? { pin: m.pin } : { pin_hash: m.pin_hash }) })),
        };
        if (ownerPin) payload.owner_pin = ownerPin;
        await adminApi.put(`/admin/shops/${initial.id}`, payload);
      } else {
        await adminApi.post(`/admin/shops`, {
          name, address, area, town, logo_path: logoPath,
          owner_name: ownerName, owner_email: ownerEmail || undefined,
          owner_pin: ownerPin,
          managers: validMgrs.map(m => ({ name: m.name, email: m.email || undefined, pin: m.pin })),
          renewal_date: renewal, monthly_fee_thb: Number(fee),
          opening_time_24h: openTime, closing_time_24h: closeTime,
        });
      }
      toast.success(isEdit ? "Shop updated" : "Shop created");
      onSaved?.();
      onOpenChange(false);
    } catch (er) {
      setErr(formatApiError(er.response?.data?.detail) || er.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="shop-form-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {isEdit ? t("admin.shop.title_edit") : t("admin.shop.title_new")}
          </DialogTitle>
          <DialogDescription>Shop URL: <span className="font-mono">/{slugPreview || "…"}</span></DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="caption-label">{t("admin.shop.shop_name")}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="shop-form-name" />
            </div>
            <div>
              <Label className="caption-label">{t("admin.shop.street")}</Label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="shop-form-address" />
            </div>
            <div>
              <Label className="caption-label">{t("common.area")}</Label>
              <Input value={area} onChange={(e) => setArea(e.target.value)} data-testid="shop-form-area" />
            </div>
            <div>
              <Label className="caption-label">{t("common.town")}</Label>
              <Input value={town} onChange={(e) => setTown(e.target.value)} data-testid="shop-form-town" />
            </div>
          </div>
          <div>
            <Label className="caption-label">{t("admin.shop.logo")}</Label>
            <FileUpload
              value={logoPath}
              onChange={setLogoPath}
              uploader={(f) => uploadAdminFile(f, `shops/logo`)}
              testId="shop-form-logo"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="caption-label">{t("admin.shop.owner_name")}</Label>
              <Input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} required data-testid="shop-form-owner-name" />
            </div>
            <div>
              <Label className="caption-label">{t("admin.shop.owner_email")} <span className="text-stone-400">({t("common.optional")})</span></Label>
              <Input type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} data-testid="shop-form-owner-email" />
            </div>
            <div>
              <Label className="caption-label">{t("admin.shop.owner_pin")} {isEdit && <span className="text-stone-400">(leave blank to keep)</span>}</Label>
              <Input inputMode="numeric" maxLength={4} value={ownerPin} onChange={(e) => setOwnerPin(e.target.value.replace(/\D/g, ""))} required={!isEdit} data-testid="shop-form-owner-pin" />
            </div>
            <div>
              <Label className="caption-label">{t("admin.shop.renewal_date")}</Label>
              <Input type="date" value={renewal} onChange={(e) => setRenewal(e.target.value)} required data-testid="shop-form-renewal-date" />
            </div>
            <div>
              <Label className="caption-label">{t("admin.shop.monthly_fee")}</Label>
              <Input type="number" min="0" step="0.01" value={fee} onChange={(e) => setFee(e.target.value)} required data-testid="shop-form-monthly-fee" />
            </div>
            <div>
              <Label className="caption-label">{t("admin.shop.opening_time")}</Label>
              <Input type="time" value={openTime} onChange={(e) => setOpenTime(e.target.value)} required data-testid="shop-form-opening-time" />
            </div>
            <div>
              <Label className="caption-label">{t("admin.shop.closing_time")}</Label>
              <Input type="time" value={closeTime} onChange={(e) => setCloseTime(e.target.value)} required data-testid="shop-form-closing-time" />
            </div>
          </div>
          <div>
            <Label className="caption-label">{t("admin.shop.managers")}</Label>
            <div className="space-y-2 mt-1">
              {managers.map((m, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-4">
                    <Input placeholder="Name" value={m.name || ""} onChange={(e) => {
                      const arr = [...managers]; arr[i] = { ...m, name: e.target.value }; setManagers(arr);
                    }} data-testid={`shop-form-manager-name-${i}`} />
                  </div>
                  <div className="col-span-4">
                    <Input placeholder="Email" type="email" value={m.email || ""} onChange={(e) => {
                      const arr = [...managers]; arr[i] = { ...m, email: e.target.value }; setManagers(arr);
                    }} data-testid={`shop-form-manager-email-${i}`} />
                  </div>
                  <div className="col-span-3">
                    <Input placeholder={isEdit && m.pin_hash ? "•••• (keep)" : "PIN (4)"} inputMode="numeric" maxLength={4}
                      value={m.pin || ""} onChange={(e) => {
                        const arr = [...managers]; arr[i] = { ...m, pin: e.target.value.replace(/\D/g, "") }; setManagers(arr);
                      }} data-testid={`shop-form-manager-pin-${i}`} />
                  </div>
                  <div className="col-span-1">
                    <Button type="button" variant="ghost" size="sm" onClick={() => setManagers(managers.filter((_, k) => k !== i))} data-testid={`shop-form-manager-remove-${i}`}>×</Button>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setManagers([...managers, { name: "", email: "", pin: "" }])} data-testid="shop-form-add-manager">
                <Plus className="w-3.5 h-3.5 mr-1" /> {t("admin.shop.add_manager")}
              </Button>
            </div>
          </div>
          {err && <div className="text-red-600 text-sm" data-testid="shop-form-error">{err}</div>}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-800 hover:bg-emerald-900" data-testid="shop-form-submit">
              {busy ? "…" : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DisableDialog({ shop, open, onOpenChange, onDone }) {
  const [reason, setReason] = useState("Overdue renewal fee");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/shops/${shop.id}/toggle-active`, { is_active: false, reason });
      toast.success("Shop disabled");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="disable-shop-dialog">
        <DialogHeader>
          <DialogTitle>Disable {shop?.name}?</DialogTitle>
          <DialogDescription>Staff, manager, and owner will lose access until reactivated.</DialogDescription>
        </DialogHeader>
        <div>
          <Label className="caption-label">Reason</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="disable-shop-reason" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={busy} data-testid="disable-shop-confirm">Disable</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDashboard() {
  const { t } = useLang();
  const [shops, setShops] = useState([]);
  const [letter, setLetter] = useState("");
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingShop, setEditingShop] = useState(null);
  const [disabling, setDisabling] = useState(null);
  const [isRoot, setIsRoot] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [r, me] = await Promise.all([
        adminApi.get("/admin/shops"),
        adminApi.get("/auth/me"),
      ]);
      setShops(r.data.shops || []);
      setIsRoot(Boolean(me.data.user?.is_root));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const total = shops.length;
    const overdue4 = shops.filter(s => s.days_overdue >= 4).length;
    const monthlyRevenue = shops.filter(s => s.is_active).reduce((a, s) => a + Number(s.monthly_fee_thb || 0), 0);
    return { total, overdue4, monthlyRevenue };
  }, [shops]);

  const overdueList = useMemo(() => shops.filter(s => s.days_overdue >= 4).sort((a, b) => (a.renewal_date || "").localeCompare(b.renewal_date || "")), [shops]);

  const filtered = useMemo(() => {
    return shops.filter(s => {
      if (letter && !(s.name || "").toUpperCase().startsWith(letter)) return false;
      if (q && !`${s.name} ${s.town} ${s.slug}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [shops, letter, q]);

  const shopPublicUrl = (shop) => `${window.location.origin}/${shop.slug}`;

  const copyShopUrl = async (shop) => {
    const url = shopPublicUrl(shop);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Shop URL copied");
    } catch {
      // Fallback for browsers blocking clipboard API in insecure contexts.
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success("Shop URL copied"); }
      catch { toast.error("Copy failed — select & copy manually: " + url); }
      finally { ta.remove(); }
    }
  };

  const downloadLaunchQR = async (shop) => {
    try {
      // Dynamic import so the qrcode lib is only bundled when the super admin
      // actually clicks the QR button — keeps the shop pages lean.
      const QR = (await import("qrcode")).default;
      const url = shopPublicUrl(shop);
      // Render on a canvas we build in-memory so we can overlay the shop name
      // and URL underneath the code — makes the download instantly usable as
      // a launch card.
      const qrPx = 512, pad = 48, footer = 110;
      const canvas = document.createElement("canvas");
      canvas.width = qrPx + pad * 2;
      canvas.height = qrPx + pad * 2 + footer;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const qrCanvas = document.createElement("canvas");
      await QR.toCanvas(qrCanvas, url, { width: qrPx, margin: 0, errorCorrectionLevel: "M", color: { dark: "#065f46", light: "#ffffff" } });
      ctx.drawImage(qrCanvas, pad, pad, qrPx, qrPx);
      ctx.fillStyle = "#1c1917";
      ctx.textAlign = "center";
      ctx.font = "600 26px ui-serif, Georgia, serif";
      ctx.fillText(shop.name || "Shop", canvas.width / 2, qrPx + pad + 48);
      ctx.fillStyle = "#44403c";
      // Auto-shrink the URL font so even long slugs fit on one line within the
      // canvas padding — avoids the "…bangkok" tail being clipped.
      let urlFont = 16;
      const maxWidth = canvas.width - pad * 2;
      do {
        ctx.font = `${urlFont}px ui-monospace, SFMono-Regular, Menlo, monospace`;
        if (ctx.measureText(url).width <= maxWidth) break;
        urlFont -= 1;
      } while (urlFont >= 8);
      ctx.fillText(url, canvas.width / 2, qrPx + pad + 80);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${shop.slug}-launch-qr.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      toast.success("Launch QR downloaded");
    } catch (e) {
      toast.error("Unable to generate QR: " + (e?.message || "unknown error"));
    }
  };

  const openClient = async (shop) => {
    // Open the popup SYNCHRONOUSLY in the click handler so popup blockers allow it.
    // We navigate the pre-opened window after the impersonate call resolves.
    const popup = window.open("about:blank", "_blank");
    try {
      const r = await adminApi.post(`/admin/shops/${shop.id}/impersonate`);
      // Save to localStorage — a new same-origin tab reads the same storage.
      localStorage.setItem(`relax_shop_token::${shop.slug}`, r.data.token);
      localStorage.setItem(`relax_shop_role::${shop.slug}`, r.data.role);
      localStorage.setItem(`relax_shop_person::${shop.slug}`, r.data.person_name);
      // Mark this as a super-admin impersonation so the idle-logout hook skips it.
      localStorage.setItem(`relax_shop_is_super::${shop.slug}`, "1");
      const url = `/${shop.slug}`;
      if (popup && !popup.closed) {
        popup.location.href = url;
      } else {
        // popup was blocked by the browser — fall back to same-tab navigation
        toast.info("Popup blocked — opening in this tab");
        setTimeout(() => { window.location.href = url; }, 1200);
      }
    } catch (e) {
      if (popup && !popup.closed) popup.close();
      toast.error(formatApiError(e.response?.data?.detail) || "Unable to open client");
    }
  };

  const confirmPayment = async (shop) => {
    try {
      await adminApi.post(`/admin/shops/${shop.id}/confirm-payment`);
      toast.success("Payment confirmed — renewal advanced 30 days");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (shop) => {
    if (shop.is_active) { setDisabling(shop); return; }
    try {
      await adminApi.post(`/admin/shops/${shop.id}/toggle-active`, { is_active: true });
      toast.success("Shop reactivated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resetOwnerPin = async (shop) => {
    try {
      const r = await adminApi.post(`/admin/shops/${shop.id}/reset-owner-pin`);
      toast.success(r.data.emailed ? "New PIN emailed to owner" : "PIN reset (no email on file)");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (shop) => {
    if (!window.confirm(`Delete ${shop.name} and all its data?`)) return;
    try {
      await adminApi.delete(`/admin/shops/${shop.id}`);
      toast.success("Shop deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <CorpHeader />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6" data-testid="admin-header-row">
          <div>
            <h1 className="font-serif text-4xl lg:text-5xl text-stone-900 tracking-tight">Client shops</h1>
            <p className="text-stone-500 text-sm mt-1">Manage every white-label tenant, renewals, and access.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              asChild
              variant="outline"
              className="border-stone-300 text-stone-700 hover:bg-stone-100"
              data-testid="download-quick-start-button"
            >
              <a href="/quick-start.pdf" target="_blank" rel="noopener noreferrer" download>
                <FileText className="w-4 h-4 mr-1.5" /> Quick-Start
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-stone-300 text-stone-700 hover:bg-stone-100"
              data-testid="download-user-manual-button"
            >
              <a href="/user-manual.pdf" target="_blank" rel="noopener noreferrer" download>
                <BookOpen className="w-4 h-4 mr-1.5" /> User Manual
              </a>
            </Button>
            <Button onClick={() => { setEditingShop(null); setFormOpen(true); }} className="bg-emerald-800 hover:bg-emerald-900 text-amber-50" data-testid="add-client-button">
              <Plus className="w-4 h-4 mr-1.5" /> {t("admin.dashboard.new_client")}
            </Button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="border-stone-200/80" data-testid="kpi-total-shops">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-11 h-11 rounded bg-emerald-100 text-emerald-800 flex items-center justify-center">
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <div className="caption-label">{t("admin.dashboard.total_shops")}</div>
                <div className="font-serif text-3xl mt-0.5 tabular">{stats.total}</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-stone-200/80" data-testid="kpi-overdue-shops">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-11 h-11 rounded bg-red-50 text-red-700 flex items-center justify-center">
                <AlertCircle className="w-5 h-5" />
              </div>
              <div>
                <div className="caption-label">{t("admin.dashboard.overdue_shops")}</div>
                <div className="font-serif text-3xl mt-0.5 tabular">{stats.overdue4}</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-stone-200/80" data-testid="kpi-monthly-revenue">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-11 h-11 rounded bg-amber-100 text-amber-800 flex items-center justify-center">
                <TrendingUp className="w-5 h-5" />
              </div>
              <div>
                <div className="caption-label">{t("admin.dashboard.monthly_revenue")}</div>
                <div className="font-serif text-3xl mt-0.5 tabular">{fmtTHBn(stats.monthlyRevenue)}</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Root-admin-only team panel */}
        {isRoot && <TeamPanel />}

        {/* Agent enquiries inbox */}
        <EnquiryInbox />

        {/* Overdue dropdown list */}
        <Card className="mb-8 border-stone-200/80">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="font-serif text-xl">{t("admin.dashboard.overdue_list")}</h2>
                <p className="caption-label mt-0.5">4+ days past due — can be disabled</p>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="overdue-dropdown-trigger">
                    {overdueList.length} shops
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80" data-testid="overdue-dropdown-content">
                  {overdueList.length === 0 && <div className="px-3 py-2 text-sm text-stone-500">{t("admin.dashboard.no_overdue")}</div>}
                  {overdueList.map(s => (
                    <DropdownMenuItem key={s.id} className="flex items-center justify-between gap-3" data-testid={`overdue-item-${s.slug}`}>
                      <div>
                        <div className="text-sm font-medium">{s.name}</div>
                        <div className="text-xs text-stone-500">Due {s.renewal_date} · {s.days_overdue}d overdue</div>
                      </div>
                      <Button size="sm" variant={s.is_active ? "destructive" : "outline"} onClick={(e) => { e.preventDefault(); toggleActive(s); }} data-testid={`overdue-disable-${s.slug}`}>
                        {s.is_active ? "Disable" : "Reactivate"}
                      </Button>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardContent>
        </Card>

        {/* Directory */}
        <Card className="mb-6 border-stone-200/80">
          <CardContent className="p-5">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <h2 className="font-serif text-xl mr-4">{t("admin.dashboard.alphabet_directory")}</h2>
              <div className="flex flex-wrap gap-1">
                <button className={`px-2 py-1 rounded text-xs font-mono ${!letter ? "bg-emerald-800 text-amber-50" : "bg-stone-100 hover:bg-stone-200"}`}
                  onClick={() => setLetter("")} data-testid="alphabet-filter-all">ALL</button>
                {ALPHABET.map(l => (
                  <button
                    key={l}
                    onClick={() => setLetter(letter === l ? "" : l)}
                    className={`w-7 h-7 rounded text-xs font-mono ${letter === l ? "bg-emerald-800 text-amber-50" : "bg-stone-100 hover:bg-stone-200 text-stone-700"}`}
                    data-testid={`alphabet-filter-${l}`}
                  >{l}</button>
                ))}
              </div>
              <div className="ml-auto relative w-64">
                <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-stone-400" />
                <Input placeholder="Search name / town / slug" value={q} onChange={(e) => setQ(e.target.value)} className="pl-7 h-9" data-testid="shop-search" />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-stone-200">
                    <th className="py-2.5 pr-3 caption-label">Shop</th>
                    <th className="py-2.5 pr-3 caption-label">Town</th>
                    <th className="py-2.5 pr-3 caption-label">Slug</th>
                    <th className="py-2.5 pr-3 caption-label">Renewal</th>
                    <th className="py-2.5 pr-3 caption-label text-right">Fee</th>
                    <th className="py-2.5 pr-3 caption-label">Status</th>
                    <th className="py-2.5 pr-3 caption-label text-right">Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="shops-table-body">
                  {filtered.map(s => (
                    <tr key={s.id} className="border-b border-stone-100 hover:bg-stone-50/60" data-testid={`shop-row-${s.slug}`}>
                      <td className="py-2.5 pr-3">
                        <div className="flex items-center gap-3">
                          {s.logo_path ? (
                            <img src={fileUrl(s.logo_path)} className="w-8 h-8 rounded object-cover ring-1 ring-stone-200" alt="" />
                          ) : (
                            <div className="w-8 h-8 rounded bg-emerald-100 text-emerald-800 flex items-center justify-center text-xs font-mono">
                              {(s.name || "?").slice(0, 2).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className="font-medium">{s.name}</div>
                            <div className="text-xs text-stone-500">{s.owner_name || "—"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 pr-3">{s.town || "—"}</td>
                      <td className="py-2.5 pr-3 font-mono text-xs">/{s.slug}</td>
                      <td className="py-2.5 pr-3 font-mono text-xs">
                        {s.renewal_date}
                        {s.days_overdue > 0 && <span className="ml-1 text-red-600">({s.days_overdue}d)</span>}
                      </td>
                      <td className="py-2.5 pr-3 text-right tabular font-mono">{fmtTHBn(s.monthly_fee_thb)}</td>
                      <td className="py-2.5 pr-3">
                        {s.is_active ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" /> Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-600" /> Disabled
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button size="sm" variant="ghost" title="Copy shop URL" onClick={() => copyShopUrl(s)} data-testid={`copy-url-${s.slug}`}>
                            <Link2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" title="Download launch QR" onClick={() => downloadLaunchQR(s)} data-testid={`download-qr-${s.slug}`}>
                            <QrCode className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" title="Open client" onClick={() => openClient(s)} data-testid={`open-client-${s.slug}`}>
                            <ExternalLink className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" title="Confirm payment" onClick={() => confirmPayment(s)} data-testid={`confirm-payment-${s.slug}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                          </Button>
                          <Button size="sm" variant="ghost" title={s.is_active ? "Disable" : "Reactivate"} onClick={() => toggleActive(s)} data-testid={`toggle-active-${s.slug}`}>
                            <Ban className={`w-3.5 h-3.5 ${s.is_active ? "text-red-600" : "text-stone-400"}`} />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" data-testid={`shop-menu-${s.slug}`}>
                                <MoreHorizontal className="w-3.5 h-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => { setEditingShop(s); setFormOpen(true); }} data-testid={`edit-shop-${s.slug}`}>
                                <Pencil className="w-3.5 h-3.5 mr-2" /> Edit shop
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => resetOwnerPin(s)} data-testid={`reset-pin-${s.slug}`}>
                                <KeyRound className="w-3.5 h-3.5 mr-2" /> Reset owner PIN
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => del(s)} className="text-red-600" data-testid={`delete-shop-${s.slug}`}>
                                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete shop
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-stone-500 text-sm">
                        {letter ? t("admin.dashboard.no_shops_letter") : "No shops yet."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>

      <ShopForm open={formOpen} onOpenChange={setFormOpen} onSaved={load} initial={editingShop} />
      <DisableDialog shop={disabling} open={!!disabling} onOpenChange={(o) => !o && setDisabling(null)} onDone={load} />
    </div>
  );
}